import { useState, useEffect } from "react";
import Modal from "@mui/material/Modal";
import Icon from "@mui/material/Icon";
import Checkbox from "@mui/material/Checkbox";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import api from "../services/api";
import { useTranslation } from "react-i18next";

const modules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["link"],
    ["clean"],
  ],
};

const formats = [
  "header", "bold", "italic", "underline", "strike",
  "color", "background", "list", "bullet", "align", "link",
];

function EmailModal({ open, onClose, eleveId, groupeId, nomDestinataire, elevesGroupe }) {
  const { t } = useTranslation();
  const [sujet, setSujet] = useState("");
  const [message, setMessage] = useState("");
  const [fichiers, setFichiers] = useState([]);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    console.log("elevesGroupe reçu:", elevesGroupe);
    console.log("groupeId:", groupeId);
    if (open && elevesGroupe && elevesGroupe.length > 0) {
      setSelectedIds(elevesGroupe.filter((e) => e.email).map((e) => e.id));
    }
    if (!open) {
      setSujet("");
      setMessage("");
      setFichiers([]);
      setResult(null);
      setError("");
      setSelectedIds([]);
    }
  }, [open, elevesGroupe]);

  const handleClose = () => {
    setSujet("");
    setMessage("");
    setFichiers([]);
    setResult(null);
    setError("");
    setSelectedIds([]);
    onClose();
  };

  const toggleEleve = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    if (elevesGroupe) {
      setSelectedIds(elevesGroupe.filter((e) => e.email).map((e) => e.id));
    }
  };

  const deselectAll = () => setSelectedIds([]);

  const handleFichiers = (e) => {
    const files = Array.from(e.target.files);
    setFichiers((prev) => [...prev, ...files]);
  };

  const removeFichier = (index) => {
    setFichiers((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    if (!message || message === "<p><br></p>") {
      setError(t("email.messageObligatoire"));
      return;
    }
    if (groupeId && selectedIds.length === 0) {
      setError(t("email.destinataireObligatoire"));
      return;
    }
    setSending(true);
    setError("");
    setResult(null);
    try {
      const formData = new FormData();
      formData.append("sujet", sujet);
      formData.append("message", message);
      if (groupeId) {
        selectedIds.forEach((id) => formData.append("eleves_ids[]", id));
      }
      fichiers.forEach((f) => formData.append("fichiers", f));

      const endpoint = eleveId
        ? `/emails/eleve/${eleveId}`
        : `/emails/groupe/${groupeId}`;

      const { data } = await api.post(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data.message);
    } catch (err) {
      setError(err.response?.data?.message || t("email.erreurEnvoi"));
    } finally {
      setSending(false);
    }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return t("email.taille.o", { n: bytes });
    if (bytes < 1024 * 1024) return t("email.taille.ko", { n: (bytes / 1024).toFixed(1) });
    return t("email.taille.mo", { n: (bytes / (1024 * 1024)).toFixed(1) });
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <MDBox sx={{
        position: "absolute", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)",
        width: { xs: "95%", sm: 680 },
        maxHeight: "92vh",
        background: "#fff", borderRadius: "20px",
        boxShadow: "0 24px 60px rgba(0,0,0,0.2)",
        outline: "none", overflow: "hidden",
        display: "flex", flexDirection: "column",
      }}>
        {/* Header */}
        <MDBox px={3} py={2.5} sx={{
          background: "linear-gradient(135deg, #1565c0, #1976d2)",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          flexShrink: 0,
        }}>
          <MDBox>
            <MDTypography variant="h6" color="white" fontWeight="bold">
              ✉️ {t("commun.envoyerEmail")}
            </MDTypography>
            <MDTypography variant="caption" color="white" sx={{ opacity: 0.85 }}>
              {t("email.a", { nom: nomDestinataire })}
              {groupeId && elevesGroupe && (
                <span style={{ marginLeft: 6, opacity: 0.8 }}>
                  ({t("email.selection", { count: selectedIds.length })})
                </span>
              )}
            </MDTypography>
          </MDBox>
          <MDBox onClick={handleClose} sx={{
            width: 32, height: 32, borderRadius: "50%",
            background: "rgba(255,255,255,0.2)", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            "&:hover": { background: "rgba(255,255,255,0.35)" },
          }}>
            <Icon sx={{ color: "#fff", fontSize: 18 }}>close</Icon>
          </MDBox>
        </MDBox>

        {/* Corps */}
        <MDBox sx={{ flex: 1, overflowY: "auto", p: 3 }}>

          {result && (
            <MDBox mb={2} p={2} sx={{
              background: "#e8f5e9", borderRadius: "10px", border: "1px solid #a5d6a7",
            }}>
              <MDTypography variant="body2" fontWeight="bold" sx={{ color: "#2e7d32" }}>
                ✅ {result}
              </MDTypography>
            </MDBox>
          )}

          {error && (
            <MDBox mb={2} p={2} sx={{
              background: "#ffebee", borderRadius: "10px", border: "1px solid #ffcdd2",
            }}>
              <MDTypography variant="caption" color="error" fontWeight="medium">
                ⚠️ {error}
              </MDTypography>
            </MDBox>
          )}

          {!result && (
            <>
              {/* Sélection destinataires groupe */}
              {groupeId && elevesGroupe && elevesGroupe.length > 0 && (
                <MDBox mb={2}>
                  <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <MDTypography variant="caption" fontWeight="bold" sx={{
                      textTransform: "uppercase", fontSize: 11,
                      letterSpacing: "0.05em", color: "#9e9e9e",
                    }}>
                      {t("email.destinataires")}
                    </MDTypography>
                    <MDBox display="flex" gap={1} alignItems="center">
                      <MDBox onClick={selectAll} sx={{
                        fontSize: 11, fontWeight: 600, color: "#1565c0",
                        cursor: "pointer", "&:hover": { textDecoration: "underline" },
                      }}>
                        {t("commun.toutCocher")}
                      </MDBox>
                      <MDTypography variant="caption" color="text">|</MDTypography>
                      <MDBox onClick={deselectAll} sx={{
                        fontSize: 11, fontWeight: 600, color: "#c62828",
                        cursor: "pointer", "&:hover": { textDecoration: "underline" },
                      }}>
                        {t("commun.toutDecocher")}
                      </MDBox>
                    </MDBox>
                  </MDBox>
                  <MDBox sx={{
                    maxHeight: 160, overflowY: "auto",
                    border: "1px solid #e0e0e0", borderRadius: "10px",
                    background: "#fafafa",
                  }}>
                    {elevesGroupe.map((eleve) => {
                      const hasEmail = Boolean(eleve.email);
                      const isSelected = selectedIds.includes(eleve.id);
                      return (
                        <MDBox key={eleve.id}
                          display="flex" alignItems="center" gap={1.5}
                          px={1.5} py={1}
                          sx={{
                            borderBottom: "1px solid #f0f0f0",
                            background: isSelected ? "#e3f2fd" : "#fff",
                            opacity: hasEmail ? 1 : 0.5,
                            cursor: hasEmail ? "pointer" : "not-allowed",
                            transition: "background 0.15s",
                            "&:last-child": { borderBottom: "none" },
                            "&:hover": hasEmail ? { background: isSelected ? "#bbdefb" : "#f5f5f5" } : {},
                          }}
                          onClick={() => hasEmail && toggleEleve(eleve.id)}>
                          <Checkbox
                            checked={isSelected}
                            disabled={!hasEmail}
                            size="small"
                            onChange={() => hasEmail && toggleEleve(eleve.id)}
                            sx={{
                              p: 0.3,
                              color: "#1565c0",
                              "&.Mui-checked": { color: "#1565c0" },
                            }}
                          />
                          <MDBox sx={{
                            width: 30, height: 30, borderRadius: "50%",
                            background: isSelected ? "#1565c0" : "#e0e0e0",
                            color: "#fff", display: "flex",
                            alignItems: "center", justifyContent: "center",
                            fontWeight: 700, fontSize: 11, flexShrink: 0,
                          }}>
                            {eleve.prenom?.[0]}{eleve.nom?.[0]}
                          </MDBox>
                          <MDBox flex={1}>
                            <MDTypography variant="caption" fontWeight="bold" sx={{ color: "#344767" }}>
                              {eleve.prenom} {eleve.nom}
                            </MDTypography>
                            <MDTypography variant="caption" color="text" display="block">
                              {hasEmail ? <>✉️ <bdi dir="ltr">{eleve.email}</bdi></> : `❌ ${t("email.pasEmail")}`}
                            </MDTypography>
                          </MDBox>
                          {isSelected && (
                            <Icon sx={{ color: "#1565c0", fontSize: 18 }}>check_circle</Icon>
                          )}
                        </MDBox>
                      );
                    })}
                  </MDBox>
                </MDBox>
              )}

              {/* Sujet */}
              <MDBox mb={2}>
                <MDTypography variant="caption" fontWeight="bold" sx={{
                  textTransform: "uppercase", fontSize: 11,
                  letterSpacing: "0.05em", color: "#9e9e9e", display: "block", mb: 0.5,
                }}>
                  {t("email.sujet")}
                </MDTypography>
                <input value={sujet} onChange={(e) => setSujet(e.target.value)}
                  placeholder={t("email.sujetPlaceholder")}
                  style={{
                    width: "100%", height: "44px", padding: "0 14px",
                    borderRadius: "8px", border: "1px solid #d2d6da",
                    fontSize: "14px", color: "#344767", outline: "none",
                    background: "#fff", boxSizing: "border-box", fontFamily: "inherit",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#1565c0")}
                  onBlur={(e) => (e.target.style.borderColor = "#d2d6da")} />
              </MDBox>

              {/* Éditeur riche */}
              <MDBox mb={2}>
                <MDTypography variant="caption" fontWeight="bold" sx={{
                  textTransform: "uppercase", fontSize: 11,
                  letterSpacing: "0.05em", color: "#9e9e9e", display: "block", mb: 0.5,
                }}>
                  {t("email.message")} *
                </MDTypography>
                <MDBox sx={{
                  "& .ql-container": { borderRadius: "0 0 8px 8px", minHeight: "160px" },
                  "& .ql-toolbar": { borderRadius: "8px 8px 0 0", border: "1px solid #d2d6da" },
                  "& .ql-container.ql-snow": { border: "1px solid #d2d6da", borderTop: "none" },
                }}>
                  <ReactQuill theme="snow" value={message} onChange={setMessage}
                    modules={modules} formats={formats}
                    placeholder={t("email.messagePlaceholder")} />
                </MDBox>
              </MDBox>

              {/* Pièces jointes */}
              <MDBox mb={2}>
                <MDTypography variant="caption" fontWeight="bold" sx={{
                  textTransform: "uppercase", fontSize: 11,
                  letterSpacing: "0.05em", color: "#9e9e9e", display: "block", mb: 1,
                }}>
                  {t("email.piecesJointes")}
                </MDTypography>
                <MDButton variant="outlined" color="secondary" size="small"
                  component="label" sx={{ borderRadius: "8px" }}>
                  <Icon sx={{ mr: 0.5 }}>attach_file</Icon> {t("email.ajouterFichier")}
                  <input type="file" multiple hidden onChange={handleFichiers} />
                </MDButton>
                {fichiers.length > 0 && (
                  <MDBox mt={1.5}>
                    {fichiers.map((f, i) => (
                      <MDBox key={i} display="flex" alignItems="center" gap={1}
                        py={0.8} px={1.5} mb={0.5}
                        sx={{ background: "#f8faff", borderRadius: "8px", border: "1px solid #e3f2fd" }}>
                        <Icon sx={{ color: "#1565c0", fontSize: 18 }}>description</Icon>
                        <MDTypography variant="caption" sx={{ color: "#344767", flex: 1 }}>
                          {f.name}
                        </MDTypography>
                        <MDTypography variant="caption" color="text">
                          {formatSize(f.size)}
                        </MDTypography>
                        <MDBox onClick={() => removeFichier(i)} sx={{
                          width: 22, height: 22, borderRadius: "50%",
                          background: "#ffebee", color: "#c62828",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          cursor: "pointer",
                        }}>
                          <Icon sx={{ fontSize: 14 }}>close</Icon>
                        </MDBox>
                      </MDBox>
                    ))}
                  </MDBox>
                )}
              </MDBox>

              {/* Info */}
              <MDBox p={1.5} sx={{
                background: "#f8faff", borderRadius: "8px", border: "1px solid #e3f2fd",
              }}>
                <MDTypography variant="caption" color="text">
                  ℹ️ {t("email.info")}
                </MDTypography>
              </MDBox>
            </>
          )}
        </MDBox>

        {/* Footer */}
        <MDBox px={3} py={2} display="flex" gap={2} justifyContent="flex-end"
          sx={{ borderTop: "1px solid #f0f0f0", flexShrink: 0 }}>
          <MDButton variant="outlined" color="secondary" size="small" onClick={handleClose}>
            {result ? t("commun.fermer") : t("commun.annuler")}
          </MDButton>
          {!result && (
            <MDButton size="small" disabled={sending} onClick={handleSend} sx={{
              background: "linear-gradient(135deg, #1565c0, #1976d2)",
              color: "#fff", fontWeight: 700, borderRadius: "8px", px: 3,
            }}>
              {sending ? t("email.envoiEnCours") : `${t("email.envoyer")} ✉️`}
            </MDButton>
          )}
        </MDBox>
      </MDBox>
    </Modal>
  );
}

export default EmailModal;